import { todayMx } from '../lib/dateUtils';
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API, useAuth } from '../App';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Plus, Search, MoreHorizontal, Edit, Trash2, Eye, Loader2, X, Package, History } from 'lucide-react';

const statusLabels = { disponible: 'Disponible', vendido: 'Vendido', instalado: 'Instalado', cancelado: 'Cancelado' };
const statusColors = { disponible: 'bg-green-100 text-green-700 border-green-300 dark:bg-green-900/30 dark:text-green-400', vendido: 'bg-blue-100 text-blue-700 border-blue-300 dark:bg-blue-900/30 dark:text-blue-400', instalado: 'bg-purple-100 text-purple-700 border-purple-300 dark:bg-purple-900/30 dark:text-purple-400', cancelado: 'bg-gray-100 text-gray-700 border-gray-300 dark:bg-gray-800 dark:text-gray-400' };

const modelos = ['Mirage', 'B Air'];
const capacidades = ['0.5 Ton', '1 Ton', '1.5 Ton', '2 Ton', '2.5 Ton', '3 Ton', '4 Ton', '5 Ton'];

export default function Inventory() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    fecha_entrada: todayMx(),
    numero_equipo: '', modelo: '', capacidad: '', serie_evaporador: '', serie_condensador: '',
    estatus: 'disponible', observaciones: ''
  });

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (statusFilter !== 'all') params.append('estatus', statusFilter);
      const res = await axios.get(`${API}/inventory?${params}`);
      setItems(res.data);
    } catch { toast.error('Error al cargar inventario'); }
    finally { setLoading(false); }
  }, [search, statusFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const counts = {
    total: items.length,
    disponible: items.filter(i => i.estatus === 'disponible').length,
    vendido: items.filter(i => i.estatus === 'vendido').length,
    instalado: items.filter(i => i.estatus === 'instalado').length,
    cancelado: items.filter(i => i.estatus === 'cancelado').length,
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.serie_evaporador || !formData.serie_condensador || !formData.modelo || !formData.capacidad) {
      toast.error('Marca, capacidad y ambas series son obligatorios');
      return;
    }
    setSaving(true);
    try {
      if (selectedItem) {
        await axios.put(`${API}/inventory/${selectedItem.id}`, formData);
        toast.success('Equipo actualizado');
      } else {
        await axios.post(`${API}/inventory`, formData);
        toast.success('Equipo registrado');
      }
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!selectedItem) return;
    try {
      await axios.delete(`${API}/inventory/${selectedItem.id}`);
      toast.success('Equipo eliminado');
      setDeleteOpen(false);
      setSelectedItem(null);
      fetchData();
    } catch { toast.error('Error al eliminar'); }
  };

  const openNew = () => {
    setSelectedItem(null);
    setFormData({ fecha_entrada: todayMx(), numero_equipo: '', modelo: '', capacidad: '', serie: '', estatus: 'disponible', observaciones: '' });
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    setSelectedItem(item);
    setFormData({ fecha_entrada: item.fecha_entrada || '', numero_equipo: item.numero_equipo || '', modelo: item.modelo || '', capacidad: item.capacidad || '', serie_evaporador: item.serie_evaporador || '', serie_condensador: item.serie_condensador || '', estatus: item.estatus || 'disponible', observaciones: item.observaciones || '' });
    setDialogOpen(true);
  };

  const openDetail = (item) => { setSelectedItem(item); setDetailOpen(true); };

  if (loading) return <div className="flex items-center justify-center h-96"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-4 animate-fade-in" data-testid="inventory-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Inventario</h2>
          <p className="text-muted-foreground text-sm">{items.length} equipos registrados</p>
        </div>
        <Button onClick={openNew} data-testid="new-inventory-btn">
          <Plus className="h-4 w-4 mr-2" />Nuevo Equipo
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
        {[
          { key: 'total', label: 'Total', count: counts.total, color: 'bg-slate-100 dark:bg-slate-800', dot: 'bg-slate-500' },
          { key: 'disponible', label: 'Disponibles', count: counts.disponible, color: 'bg-green-50 dark:bg-green-900/20', dot: 'bg-green-500' },
          { key: 'vendido', label: 'Vendidos', count: counts.vendido, color: 'bg-blue-50 dark:bg-blue-900/20', dot: 'bg-blue-500' },
          { key: 'instalado', label: 'Instalados', count: counts.instalado, color: 'bg-purple-50 dark:bg-purple-900/20', dot: 'bg-purple-500' },
          { key: 'cancelado', label: 'Cancelados', count: counts.cancelado, color: 'bg-gray-50 dark:bg-gray-800', dot: 'bg-gray-500' },
        ].map(c => (
          <Card
            key={c.key}
            className={`cursor-pointer transition-all hover:shadow-md ${statusFilter === c.key || (c.key === 'total' && statusFilter === 'all') ? 'ring-2 ring-primary' : ''} ${c.color}`}
            onClick={() => setStatusFilter(c.key === 'total' ? 'all' : c.key)}
            data-testid={`inv-card-${c.key}`}
          >
            <CardContent className="p-3">
              <div className="flex items-center gap-2">
                <div className={`w-3 h-3 rounded-full ${c.dot}`} />
                <span className="text-sm font-medium">{c.label}</span>
              </div>
              <div className="text-2xl font-bold mt-1">{c.count}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Buscar por serie, marca, cliente..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="inv-search" />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {Object.entries(statusLabels).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
            {(statusFilter !== 'all' || search) && (
              <Button variant="ghost" size="sm" onClick={() => { setStatusFilter('all'); setSearch(''); }}><X className="h-4 w-4 mr-1" />Limpiar</Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[90px]">Entrada</TableHead>
                  <TableHead className="w-[80px]">No. Equipo</TableHead>
                  <TableHead className="w-[130px]">Marca</TableHead>
                  <TableHead className="w-[80px]">Capacidad</TableHead>
                  <TableHead className="w-[130px]">Serie Evap.</TableHead>
                  <TableHead className="w-[130px]">Serie Cond.</TableHead>
                  <TableHead className="w-[100px]">Estatus</TableHead>
                  <TableHead className="w-[90px]">Salida</TableHead>
                  <TableHead className="w-[80px]">Folio</TableHead>
                  <TableHead className="w-[130px]">Cliente</TableHead>
                  <TableHead className="w-[60px]">Acc.</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.length === 0 ? (
                  <TableRow><TableCell colSpan={11} className="text-center py-8 text-muted-foreground">No hay equipos registrados</TableCell></TableRow>
                ) : items.map(item => (
                  <TableRow key={item.id} data-testid={`inv-row-${item.id}`}>
                    <TableCell className="text-sm">{item.fecha_entrada ? format(parseISO(item.fecha_entrada), 'dd/MM/yy') : '-'}</TableCell>
                    <TableCell className="font-mono text-xs font-bold">{item.numero_equipo || '-'}</TableCell>
                    <TableCell className="text-sm font-medium">{item.modelo}</TableCell>
                    <TableCell className="text-sm">{item.capacidad}</TableCell>
                    <TableCell className="font-mono text-xs">{item.serie_evaporador}</TableCell>
                    <TableCell className="font-mono text-xs">{item.serie_condensador}</TableCell>
                    <TableCell>
                      <Badge className={`${statusColors[item.estatus] || ''} border`}>{statusLabels[item.estatus] || item.estatus}</Badge>
                    </TableCell>
                    <TableCell className="text-sm">{item.fecha_salida ? format(parseISO(item.fecha_salida), 'dd/MM/yy') : '-'}</TableCell>
                    <TableCell className="font-mono text-xs">{item.folio_servicio || '-'}</TableCell>
                    <TableCell className="text-sm truncate max-w-[130px]">{item.cliente_asignado || '-'}</TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8"><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openDetail(item)}><Eye className="h-4 w-4 mr-2" />Ver Detalle</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openEdit(item)}><Edit className="h-4 w-4 mr-2" />Editar</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => { setSelectedItem(item); setDeleteOpen(true); }} className="text-destructive"><Trash2 className="h-4 w-4 mr-2" />Eliminar</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{selectedItem ? 'Editar Equipo' : 'Nuevo Equipo'}</DialogTitle>
            <DialogDescription>{selectedItem ? 'Modifica los datos del equipo' : 'Registra un nuevo equipo en inventario'}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Fecha de Entrada *</Label>
                <Input type="date" value={formData.fecha_entrada} onChange={(e) => setFormData({...formData, fecha_entrada: e.target.value})} required />
              </div>
              <div className="space-y-2">
                <Label>No. de Equipo</Label>
                <Input value={formData.numero_equipo} onChange={(e) => setFormData({...formData, numero_equipo: e.target.value})} placeholder="ID interno" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Marca *</Label>
                <Select value={formData.modelo} onValueChange={(v) => setFormData({...formData, modelo: v})}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar marca" /></SelectTrigger>
                  <SelectContent>{modelos.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Capacidad *</Label>
                <Select value={formData.capacidad} onValueChange={(v) => setFormData({...formData, capacidad: v})}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                  <SelectContent>{capacidades.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Serie Evaporador *</Label>
                <Input value={formData.serie_evaporador} onChange={(e) => setFormData({...formData, serie_evaporador: e.target.value})} placeholder="Serie del evaporador" required data-testid="serie-evaporador-input" />
              </div>
              <div className="space-y-2">
                <Label>Serie Condensador *</Label>
                <Input value={formData.serie_condensador} onChange={(e) => setFormData({...formData, serie_condensador: e.target.value})} placeholder="Serie del condensador" required data-testid="serie-condensador-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Observaciones</Label>
              <Textarea value={formData.observaciones} onChange={(e) => setFormData({...formData, observaciones: e.target.value})} placeholder="Notas adicionales" rows={2} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={saving} data-testid="save-inventory-btn">{saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Guardando...</> : selectedItem ? 'Actualizar' : 'Registrar'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Package className="h-5 w-5" />Detalle del Equipo</DialogTitle>
          </DialogHeader>
          {selectedItem && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-xs text-muted-foreground">Series</p>
                  <p className="font-mono font-bold">Evap: {selectedItem.serie_evaporador}</p>
                  <p className="font-mono font-bold">Cond: {selectedItem.serie_condensador}</p>
                </div>
                <Badge className={`${statusColors[selectedItem.estatus]} border`}>{statusLabels[selectedItem.estatus]}</Badge>
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><p className="text-xs text-muted-foreground">Marca</p><p className="font-medium">{selectedItem.modelo}</p></div>
                <div><p className="text-xs text-muted-foreground">Capacidad</p><p className="font-medium">{selectedItem.capacidad}</p></div>
                <div><p className="text-xs text-muted-foreground">No. Equipo</p><p>{selectedItem.numero_equipo || '-'}</p></div>
                <div><p className="text-xs text-muted-foreground">Fecha Entrada</p><p>{selectedItem.fecha_entrada || '-'}</p></div>
                <div><p className="text-xs text-muted-foreground">Fecha Salida</p><p>{selectedItem.fecha_salida || '-'}</p></div>
                <div><p className="text-xs text-muted-foreground">Folio Servicio</p><p className="font-mono">{selectedItem.folio_servicio || '-'}</p></div>
                <div className="col-span-2"><p className="text-xs text-muted-foreground">Cliente Asignado</p><p>{selectedItem.cliente_asignado || '-'}</p></div>
              </div>
              {selectedItem.observaciones && (
                <div className="p-3 bg-muted/50 rounded-lg">
                  <p className="text-xs text-muted-foreground">Observaciones</p>
                  <p className="text-sm mt-1">{selectedItem.observaciones}</p>
                </div>
              )}
              {/* History */}
              {selectedItem.historial && selectedItem.historial.length > 0 && (
                <div className="border rounded-lg p-3">
                  <p className="text-sm font-medium flex items-center gap-2 mb-3"><History className="h-4 w-4" />Historial ({selectedItem.historial.length})</p>
                  <ScrollArea className="max-h-48">
                    <div className="space-y-2">
                      {selectedItem.historial.map((h, idx) => (
                        <div key={idx} className="text-xs p-2 bg-muted/30 rounded border-l-2 border-primary">
                          <div className="flex justify-between"><span className="font-medium">{h.accion}</span><span className="text-muted-foreground">{h.fecha?.slice(0, 10)}</span></div>
                          {h.usuario && <p className="text-muted-foreground">Por: {h.usuario}</p>}
                          {h.cliente && <p className="text-muted-foreground">Cliente: {h.cliente}</p>}
                          {h.folio && <p className="text-muted-foreground">Folio: {h.folio}</p>}
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Eliminar Equipo</DialogTitle><DialogDescription>Esta acción no se puede deshacer</DialogDescription></DialogHeader>
          <p className="text-sm">¿Seguro que deseas eliminar el equipo <strong>{selectedItem?.serie_evaporador} / {selectedItem?.serie_condensador}</strong> ({selectedItem?.modelo})?</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteOpen(false)}>Cancelar</Button>
            <Button variant="destructive" onClick={handleDelete}>Eliminar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
